import { useEditorState, type Editor } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import {
  Bold,
  Italic,
  Strikethrough,
  Code,
  Link as LinkIcon,
  Unlink,
} from "lucide-react";
import { useCallback } from "react";

interface BubbleToolbarProps {
  editor: Editor;
}

function BubbleButton({
  onClick,
  active = false,
  title,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`p-1.5 rounded transition-colors ${
        active
          ? "bg-white/20 text-white"
          : "text-white/70 hover:bg-white/10 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

export function BubbleToolbar({ editor }: BubbleToolbarProps) {
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive("bold"),
      italic: e.isActive("italic"),
      strike: e.isActive("strike"),
      code: e.isActive("code"),
      link: e.isActive("link"),
    }),
  });

  const toggleLink = useCallback(() => {
    if (editor.isActive("link")) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const url = window.prompt("Link URL:");
    if (url) {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: url })
        .run();
    }
  }, [editor]);

  if (!state) return null;

  const s = 15;

  return (
    <BubbleMenu
      editor={editor}
      options={{
        placement: "top",
      }}
      className="flex items-center gap-0.5 px-1.5 py-1 bg-black/90 backdrop-blur-sm rounded-lg shadow-xl border border-white/10"
    >
      <BubbleButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        active={state.bold}
        title="Bold"
      >
        <Bold size={s} />
      </BubbleButton>
      <BubbleButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        active={state.italic}
        title="Italic"
      >
        <Italic size={s} />
      </BubbleButton>
      <BubbleButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        active={state.strike}
        title="Strikethrough"
      >
        <Strikethrough size={s} />
      </BubbleButton>
      <BubbleButton
        onClick={() => editor.chain().focus().toggleCode().run()}
        active={state.code}
        title="Inline Code"
      >
        <Code size={s} />
      </BubbleButton>
      <div className="w-px h-4 bg-white/20 mx-0.5" />
      <BubbleButton
        onClick={toggleLink}
        active={state.link}
        title={state.link ? "Remove Link" : "Add Link"}
      >
        {state.link ? <Unlink size={s} /> : <LinkIcon size={s} />}
      </BubbleButton>
    </BubbleMenu>
  );
}
